import { getAgents } from '@cronos-app/api';
import type { ChainLayer } from '@cronos-app/sdk';
import type { UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';
import { RefetchInterval } from '@ui/config/ui';
import { sleep } from '@ui/utils/sleep';
import { Fraction } from 'bi-fraction';

import { useGlobalChainLayer } from '../useGlobalChainLayer';
import { Agent } from './useAgents';

export interface UserAgent extends Omit<Agent, 'equals'> {
  vol24h: Fraction;
}

type PageParam =
  | undefined
  | {
      first: number;
      skip: number;
    };

export type UseUserAgentsOptions = UseInfiniteQueryOptions<UserAgent[]> & {
  type: 'holding' | 'create';
  account: string | undefined | null;
  n?: number;
};

export const useUserAgents = ({
  type,
  account,
  n = 10,
  ...options
}: UseUserAgentsOptions) => {
  const chainLayer = useGlobalChainLayer();

  return useInfiniteQuery<UserAgent[]>({
    queryKey: [useUserAgents.queryKey, type, n, chainLayer, account],
    queryFn: async ({ pageParam: _pageParam }) => {
      if (!account) throw new Error('account is empty');

      const pageParam: NonNullable<PageParam> = _pageParam || {
        first: n,
        skip: 0,
      };

      return fetchUserAgents({
        type,
        chainLayer,
        account,
        first: pageParam.first,
        skip: pageParam.skip,
      });
    },
    getNextPageParam: (lastPage, allPages): PageParam => {
      if (lastPage.length < n) {
        // there is no next page available.
        return undefined;
      }

      return {
        first: n,
        skip: allPages.length * n,
      };
    },
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: RefetchInterval.Fast,
    ...options,
    enabled: options.enabled === false ? false : !!account,
  });
};
useUserAgents.queryKey = 'useUserAgents';

const fetchUserAgents = async ({
  type,
  chainLayer,
  account,
  first,
  skip,
}: {
  type: UseUserAgentsOptions['type'];
  chainLayer: ChainLayer;
  account: string;
  first: number;
  skip: number;
}): Promise<UserAgent[]> => {
  // todo cover.lv mock data
  await sleep(1_000);
  console.debug('fetchUserAgents', account);

  // todo cover.lv mock data
  const mockAgents =
    type === 'holding'
      ? await getAgents(chainLayer, first, skip, [true, false])
      : await getAgents(chainLayer, first, skip, [false]);

  return mockAgents.content.map((agent) => {
    return {
      ...new Agent(agent),
      vol24h: new Fraction((agent.market_cap_usd ?? 0) / 3),
    };
  });
};
